import React, { ReactNode, useEffect, useState } from 'react';
import classes from './PostCardMediaPreviewInstagram.module.scss';
import storyGalleriaClasses from './IgStoryGalleria.module.scss';
import galleriaClasses from './IgGalleria.module.scss';
import { PostCardPreviewMediaDataType } from '../PostCardMediaPreview/PostCardMediaPreview.view';
import { SocialProfileWithBoards } from '../../../../hooks/useCategoriesProfilesWithBoards';
import { SocialProfile } from '../../../../components/socialProfiles/SocialProfile';
import { SocialNetworksEnum } from '../../../../constants/social-networks';
import {
  CoreMediaLibraryItemFields_Photo_Fragment,
  MediaType,
  Network,
  PostFeedType,
  PostMultiMediaAttachMode,
  PostPublishMethod
} from '../../../../models/graphql';
import classNames from 'classnames';
import { Icon } from '../../../../components/images/Icon/Icon';
import { IconsEnum } from '../../../../constants/icons';
import { serializeText, serializeTextToHtml } from '../../TextEditor/helpers/serializer';
import { Galleria } from 'primereact/galleria';
import { MediaTypePostEditorState } from '../../../../models/post-editor';
import { FirstCommentPreview } from '../FirstCommentPreview';
import { canAddComment } from '../../helpers/canAddComment';
import { MediaUploadStatus } from '../../../../constants/media';
import { CanvasImage } from '../../../../components/containers/CanvasImage';
import { InvalidMediaSet } from '../PostCardMediaPreview/InvalidMediaSet';
import { SingularValidator } from '../../validators/helpers/singular-validator';
import { CanvasImageViewProps } from '../../../../components/containers/CanvasImage/CanvasImage.view';
import { Badge } from '../../../../components/containers/Badge';
import { BadgeColorsEnum, BadgeSizesEnum } from '../../../../constants/badge';
import { profilesHelpers } from '../../../Settings/Profiles/Profiles.helpers';

export type PostCardMediaPreviewInstagramProfile = {
  socialProfile: SocialProfileWithBoards & { __typename: 'InstagramSocialProfile' };
  igType: PostFeedType;
  publishMethod: PostPublishMethod | null;
  activeVariationIndex: number;
  hasVariations: boolean;
};

export type PostCardMediaPreviewInstagramViewProps = PostCardPreviewMediaDataType &
  PostCardMediaPreviewInstagramProfile;

export type MediaTypePhotoPostEditorState = CoreMediaLibraryItemFields_Photo_Fragment & {
  loading: boolean;
  status: MediaUploadStatus;
  gif: boolean;
  length: number | null;
  transcoded: boolean;
  externalUpload: boolean;
};
const isPhotoGuard = (media: MediaTypePostEditorState): media is MediaTypePhotoPostEditorState =>
  media.type === MediaType.PHOTO;

const getImagePadding = (
  localmedia: MediaTypePhotoPostEditorState,
  objectFit: CanvasImageViewProps['objectFit'],
  omitRatio = false
): JSX.Element => {
  let mediaAspectRatio: number;
  const currentMediaAspectRatio = localmedia.height && localmedia.width ? localmedia.width / localmedia.height : 1;
  if (omitRatio) {
    mediaAspectRatio = currentMediaAspectRatio;
  } else {
    if (currentMediaAspectRatio < 4 / 5) {
      mediaAspectRatio = 4 / 5;
    } else if (currentMediaAspectRatio > 1.91) {
      mediaAspectRatio = 1.91;
    } else {
      mediaAspectRatio = currentMediaAspectRatio;
    }
  }
  return <CanvasImage media={localmedia} aspectRatio={mediaAspectRatio} objectFit={objectFit} />;
};

export const isInvalidInstagramMedia = (media: MediaTypePostEditorState[]): boolean => {
  return (
    SingularValidator.noMixedMediaInvalid(media, PostMultiMediaAttachMode.MULTIPLE) ||
    SingularValidator.instagramMediaLimitInvalid(media, PostMultiMediaAttachMode.MULTIPLE) ||
    SingularValidator.noGifsAllowedInvalid(media)
  );
};

export const getInstagramRationObject = (
  media: MediaTypePostEditorState[],
  igType: PostFeedType
): { imageAspectRatio: number; offLimits: boolean } => {
  let offLimits = false;
  let imageAspectRatio = media.length ? media[0].width / media[0].height : 1;
  if (imageAspectRatio > 1.91 && igType !== PostFeedType.STORY) {
    imageAspectRatio = 1.91;
    offLimits = true;
  }
  if (imageAspectRatio < 4 / 5 && igType !== PostFeedType.STORY) {
    imageAspectRatio = 4 / 5;
    offLimits = true;
  }
  if (igType === PostFeedType.STORY) {
    imageAspectRatio = 9 / 16;
  }
  return { imageAspectRatio, offLimits };
};

export const PostCardMediaPreviewInstagramView = (props: PostCardMediaPreviewInstagramViewProps): JSX.Element => {
  const { text, media, socialProfile, igType, activeVariationIndex, hasVariations, firstComment, publishMethod } =
    props;
  const hasText = !!serializeText(text);
  const [page, setPage] = useState<number>(0);
  const hasMultipleImages = media.length > 1;
  const provider = profilesHelpers.mapOAuthFromTypename(socialProfile.__typename, socialProfile.apiType);

  useEffect(() => {
    setPage(0);
  }, [activeVariationIndex]);

  const noMediaBlock = (
    <div
      className={classNames(classes.NoContent, 'grid-x', 'align-middle', 'align-center', {
        [classes.NoContentStory]: igType === PostFeedType.STORY
      })}>
      <p>You need to add an image/video for Instagram</p>
    </div>
  );

  const invalidMediaElement = (
    <InvalidMediaSet
      className={classNames(classes.NoContent, { [classes.NoContentStory]: igType === PostFeedType.STORY })}
    />
  );

  const isIvalidMedia = isInvalidInstagramMedia(media);
  const { imageAspectRatio } = getInstagramRationObject(media, igType);
  const showNotification = publishMethod === PostPublishMethod.MANUAL || socialProfile.directPublishEnabled === false;

  const galleriaTemplate =
    (media: MediaTypePostEditorState, objectFit: CanvasImageViewProps['objectFit']) =>
    (media: MediaTypePostEditorState): ReactNode => {
      return (
        <div className={'p-galleria-aspect-ratio-item'} style={{ aspectRatio: imageAspectRatio }}>
          {isPhotoGuard(media) ? (
            getImagePadding(media, objectFit)
          ) : media.url ? (
            <video controls={false} src={media.url} />
          ) : (
            <img src={media.thumbnailUrl} alt={'video cover'} />
          )}
        </div>
      );
    };

  const galleriaStoryTemplate = (media: MediaTypePostEditorState): ReactNode => {
    return (
      <div
        className={classNames(
          'p-galleria-aspect-ratio-item',
          publishMethod === PostPublishMethod.MANUAL || hasMultipleImages ? 'p-galleria-story-manual' : ''
        )}
        style={{ aspectRatio: imageAspectRatio }}>
        {isPhotoGuard(media) ? (
          getImagePadding(
            media,
            publishMethod === PostPublishMethod.MANUAL || hasMultipleImages ? 'cover' : 'contain',
            publishMethod === PostPublishMethod.MANUAL || hasMultipleImages
          )
        ) : media.url ? (
          <video controls={false} src={media.url} />
        ) : (
          <img src={media.thumbnailUrl} alt={'video cover'} />
        )}
      </div>
    );
  };

  switch (igType) {
    case PostFeedType.FEED:
    case PostFeedType.REEL:
      return (
        <>
          <div className={classes.SocialProfileWrapper}>
            <div className={'grid-x align-justify align-middle'}>
              <div
                className={classNames('cell shrink', showNotification ? classes.SocialProfileWrapperMaxWidth : null)}>
                <SocialProfile
                  socialProfile={{
                    id: socialProfile.id,
                    provider,
                    network: Network.INSTAGRAM,
                    name: socialProfile.displayName,
                    type: socialProfile.type
                  }}
                  disconnected={!socialProfile.connected}
                  size={'medium'}
                  label={true}
                  sublabel={true}
                  image={socialProfile.photoUrl}
                />
              </div>
              {showNotification ? (
                <div className={classNames('cell shrink', classes.ReminderNotificationWrapper)}>
                  <Badge
                    badgeKey={`notification-${socialProfile.id}-${socialProfile.__typename}`}
                    size={BadgeSizesEnum.LARGE}
                    icon={IconsEnum.bell}
                    side={'left'}
                    color={BadgeColorsEnum.GREY20}
                    tooltipText={"You'll receive a notification when this post is about to get published"}
                    text={'Notification'}
                  />
                </div>
              ) : null}
            </div>
          </div>
          {media.length > 1 ? (
            isIvalidMedia ? (
              invalidMediaElement
            ) : (
              <div className={classNames(classes.MediaWrapper, galleriaClasses)}>
                <Galleria
                  value={media}
                  item={galleriaTemplate(media[page], 'contain')}
                  className={'ig-galleria'}
                  activeIndex={page}
                  onItemChange={e => setPage(e.index)}
                  showThumbnails={false}
                  showItemNavigators={media.length > 1}
                  showIndicators={media.length > 1}
                />
              </div>
            )
          ) : media.length ? (
            isIvalidMedia ? (
              invalidMediaElement
            ) : (
              <div className={classes.MediaWrapper}>
                <div className={classes.SinglePhoto} style={{ aspectRatio: imageAspectRatio }}>
                  {isPhotoGuard(media[0]) ? (
                    getImagePadding(media[0], 'contain')
                  ) : media[0].url ? (
                    <video controls={false} src={media[0].url} />
                  ) : (
                    <img src={media[0].thumbnailUrl} alt="video cover" />
                  )}
                </div>
                {media[0].type === MediaType.VIDEO ? (
                  <div className={classes.MediaMarker}>
                    <Icon name={IconsEnum.play} size={'large'} />
                  </div>
                ) : null}
              </div>
            )
          ) : (
            noMediaBlock
          )}
          <div className={classNames(classes.FooterWrapper, 'grid-x', 'align-justify', 'align-middle')}>
            <div className={'cell auto'}>
              <div className={'grid-x align-middle'}>
                <div className={classNames('cell', 'shrink', classes.FooterItem)}>
                  <Icon name={IconsEnum.likes} size={'medium'} />
                </div>
                <div className={classNames('cell', 'shrink', classes.FooterItem)}>
                  <Icon name={IconsEnum.comments} size={'medium'} />
                </div>
                <div className={classNames('cell', 'shrink', classes.FooterItem)}>
                  <Icon name={IconsEnum.igSend} size={'medium'} />
                </div>
              </div>
            </div>
            <div className={'cell shrink'}>
              <div className={classNames('cell', 'shrink', classes.FooterItem)}>
                <Icon name={IconsEnum.igSave} size={'medium'} />
              </div>
            </div>
          </div>
          {hasText ? (
            <div
              className={classes.TextWrapper}
              dangerouslySetInnerHTML={{
                __html: serializeTextToHtml(text, SocialNetworksEnum.instagram).replace(
                  /(<p class="instagram-text">)(.+<\/p>)/,
                  `$1<span class="${classes.TextWrapperProfile}">${socialProfile.displayName}</span> $2`
                )
              }}></div>
          ) : null}
          {firstComment && canAddComment([socialProfile], igType) ? (
            <FirstCommentPreview firstComment={firstComment} socialProfile={socialProfile} />
          ) : null}
        </>
      );
    case PostFeedType.STORY:
      const showNotificationStory =
        showNotification || (socialProfile.directPublishEnabled && socialProfile.type === 'media_creator');
      const storyText = serializeText(text);
      return (
        <div
          className={classNames(
            classes.StoryWrapper,
            storyGalleriaClasses,
            hasVariations ? classes.StoryWrapperWithVariations : null
          )}>
          <div className={classes.SocialProfileStoryWrapper}>
            <div className={'grid-x align-justify align-middle'}>
              <div
                className={classNames(
                  'cell shrink',
                  showNotificationStory ? classes.SocialProfileWrapperMaxWidth : null
                )}>
                <SocialProfile
                  socialProfile={{
                    id: socialProfile.id,
                    provider,
                    network: Network.INSTAGRAM,
                    name: socialProfile.displayName,
                    type: socialProfile.type
                  }}
                  variant={'white'}
                  disconnected={!socialProfile.connected}
                  size={'medium'}
                  label={true}
                  sublabel={true}
                  image={socialProfile.photoUrl}
                />
              </div>
              {showNotificationStory ? (
                <div className={classNames('cell shrink', classes.ReminderNotificationWrapper)}>
                  <Badge
                    badgeKey={`notification-${socialProfile.id}-${socialProfile.__typename}`}
                    size={BadgeSizesEnum.LARGE}
                    icon={IconsEnum.bell}
                    side={'left'}
                    color={BadgeColorsEnum.GREY20}
                    tooltipText={"You'll receive a notification when this post is about to get published"}
                    text={'Notification'}
                  />
                </div>
              ) : null}
            </div>
            {/*<SocialProfile*/}
            {/*  socialProfile={{*/}
            {/*    id: socialProfile.id,*/}
            {/*    network: SocialNetworksEnum.instagram,*/}
            {/*    name: socialProfile.displayName,*/}
            {/*    type: socialProfile.type*/}
            {/*  }}*/}
            {/*  variant={'white'}*/}
            {/*  disconnected={!socialProfile.connected}*/}
            {/*  size={'medium'}*/}
            {/*  label={true}*/}
            {/*  sublabel={true}*/}
            {/*  image={socialProfile.photoUrl}*/}
            {/*/>*/}
          </div>
          {isIvalidMedia ? (
            invalidMediaElement
          ) : (
            <>
              <Galleria
                value={media}
                item={galleriaStoryTemplate}
                className={'ig-story'}
                showItemNavigators={media.length > 1}
                showIndicators={media.length > 1}
                activeIndex={page}
                onItemChange={e => setPage(e.index)}
                showThumbnails={false}
              />
              {media.length ? (
                media[page].type === MediaType.VIDEO ? (
                  <div className={classes.MediaMarker}>
                    <Icon name={IconsEnum.play} size={'large'} />
                  </div>
                ) : null
              ) : (
                noMediaBlock
              )}
            </>
          )}
          {!!storyText ? (
            <div className={classes.StoryTextWrapper}>
              <div
                className={classes.StoryTextWrapperContent}
                dangerouslySetInnerHTML={{ __html: serializeTextToHtml(text, SocialNetworksEnum.instagram) }}></div>
            </div>
          ) : null}
        </div>
      );
  }
};
